import { useMemo, useState } from "react";
import { MktButton } from "./primitives";
import {
  canConfirm,
  confirmationPlan,
  confirmationTier,
  SLOT_STRONG_THRESHOLD,
  type ActionAvailability,
  type ConfirmationTier,
  type OwnerAction,
} from "./owner-console-logic";
import { useT } from "../../i18n";
import type { ZoneKey } from "./mkt-format";

const LABEL_KEY: Record<OwnerAction, string> = {
  release: "marketplace.owner.releaseLabel",
  relock: "marketplace.owner.relockLabel",
  private_sale: "marketplace.owner.privateSaleLabel",
};

const TIER_KEY: Record<ConfirmationTier, string> = {
  normal: "marketplace.owner.tierNormal",
  strong: "marketplace.owner.tierStrong",
  maximum: "marketplace.owner.tierMaximum",
};

export function OwnerActionDialog({
  action,
  zone,
  slots,
  availability,
  submitting,
  error,
  onClose,
  onConfirm,
}: {
  action: OwnerAction;
  zone: ZoneKey;
  slots: number;
  availability: ActionAvailability;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const tier = useMemo(() => confirmationTier(zone, slots), [zone, slots]);
  const plan = useMemo(() => confirmationPlan(tier, { slots }), [tier, slots]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [typed, setTyped] = useState("");
  const enabled = availability.available && canConfirm(plan, { acknowledged, typed }) && !submitting;
  const label = t(LABEL_KEY[action]);
  const warning =
    tier === "maximum"
      ? t("marketplace.confirm.maximum")
      : tier === "strong"
        ? t("marketplace.confirm.strong", { slots, threshold: SLOT_STRONG_THRESHOLD })
        : t("marketplace.confirm.normal");

  return (
    <div
      data-testid="owner-action-dialog"
      data-tier={tier}
      data-action={action}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
    >
      <div className="w-full max-w-md neon-glass-strong rounded-2xl border border-border-strong p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg text-text-primary">{label}</h3>
          <span
            data-testid="owner-dialog-tier"
            className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-neon-teal/80"
          >
            {t("marketplace.owner.confirmationSuffix", { tier: t(TIER_KEY[tier]) })}
          </span>
        </div>

        <div
          data-testid="owner-dialog-warning"
          data-tier={tier}
          className={
            tier === "normal"
              ? "rounded-lg border border-border-divider bg-elevated/50 px-4 py-3 font-mono text-[11px] text-text-secondary"
              : "rounded-lg border border-state-warning/40 bg-state-warning/10 px-4 py-3 font-mono text-[11px] text-state-warning"
          }
        >
          {warning}
        </div>

        {!availability.available && (
          <div
            data-testid="owner-dialog-failclosed"
            className="rounded-lg border border-neon-coral/40 bg-neon-coral/10 px-4 py-3 font-mono text-[11px] text-neon-coral"
          >
            {t("marketplace.owner.failClosedBanner", { reason: availability.reason ?? t("marketplace.owner.unavailable") })}
          </div>
        )}

        {plan.requirePhrase != null && (
          <label className="block space-y-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-text-muted">
              {t("marketplace.owner.typeToProceedPre")}<span className="text-text-primary">{plan.requirePhrase}</span>{t("marketplace.owner.typeToProceedPost")}
            </span>
            <input
              data-testid="owner-dialog-phrase"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full h-10 px-3 rounded-md bg-elevated/60 border border-border-divider font-mono text-[12px] text-text-primary focus:outline-none focus:border-neon-teal/50"
            />
          </label>
        )}

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            data-testid="owner-dialog-ack"
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-neon-teal"
          />
          <span className="font-mono text-[11px] leading-relaxed text-text-secondary">
            {t("marketplace.owner.ack")}
          </span>
        </label>

        {error && (
          <p data-testid="owner-dialog-error" className="font-mono text-[11px] text-state-danger">
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-3 pt-1">
          <MktButton variant="ghost" testId="owner-dialog-cancel" onClick={onClose}>
            {t("marketplace.owner.cancel")}
          </MktButton>
          <MktButton variant="coral" testId="owner-dialog-confirm" disabled={!enabled} onClick={onConfirm}>
            {submitting ? t("marketplace.owner.submitting") : t("marketplace.owner.confirmAction", { action: label })}
          </MktButton>
        </div>
      </div>
    </div>
  );
}
